import React, { useEffect, useMemo, useRef, useState } from 'react';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

let leafletPromise = null;

function loadLeaflet() {
  if (typeof window !== 'undefined' && window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L));
      existing.addEventListener('error', () => reject(new Error('No se pudo cargar el mapa')));
      if (window.L) resolve(window.L);
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('No se pudo cargar el mapa'));
    document.head.appendChild(script);
  });

  return leafletPromise;
}

function parsePuntos(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function collectPuntos(visita) {
  const fromTrail = parsePuntos(visita?.puntos_gps).filter((p) => p.lat != null && p.lng != null);
  if (fromTrail.length) return fromTrail;

  const fallback = [];
  if (visita?.lat_inicio != null && visita?.lng_inicio != null) {
    fallback.push({ lat: visita.lat_inicio, lng: visita.lng_inicio, t: visita.inicio });
  }
  if (visita?.lat_fin != null && visita?.lng_fin != null) {
    fallback.push({ lat: visita.lat_fin, lng: visita.lng_fin, t: visita.fin });
  }
  if (!fallback.length && visita?.vendedor_lat != null && visita?.vendedor_lng != null) {
    fallback.push({
      lat: visita.vendedor_lat,
      lng: visita.vendedor_lng,
      t: visita.vendedor_ubicacion_at || visita.ubicacion_at || visita.inicio,
    });
  }
  return fallback;
}

function fmtHora(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'medium' });
}

function mapsUrl(lat, lng) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function routeUrl(puntos) {
  if (!puntos.length) return null;
  if (puntos.length === 1) return mapsUrl(puntos[0].lat, puntos[0].lng);
  let path = puntos;
  if (puntos.length > 12) {
    const step = Math.ceil(puntos.length / 10);
    path = puntos.filter((_, i) => i === 0 || i === puntos.length - 1 || i % step === 0);
  }
  const origin = `${path[0].lat},${path[0].lng}`;
  const dest = `${path[path.length - 1].lat},${path[path.length - 1].lng}`;
  const mid = path.slice(1, -1).map((p) => `${p.lat},${p.lng}`).join('|');
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`;
  if (mid) url += `&waypoints=${encodeURIComponent(mid)}`;
  return url;
}

function VisitaMapCanvas({ puntos, selectedIndex, onSelect }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const layerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let map;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapRef.current) return;
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        });

        map = L.map(mapRef.current, { zoomControl: true, attributionControl: true });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap',
        }).addTo(map);

        mapInstance.current = map;
        layerRef.current = L.layerGroup().addTo(map);
        setError('');
        setReady(true);
        setTimeout(() => map.invalidateSize(), 80);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Error al cargar el mapa');
      });

    return () => {
      cancelled = true;
      setReady(false);
      if (map) map.remove();
      mapInstance.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    const L = window.L;
    const map = mapInstance.current;
    const layer = layerRef.current;
    if (!L || !map || !layer || !puntos.length) return undefined;

    layer.clearLayers();
    const latlngs = puntos.map((p) => [Number(p.lat), Number(p.lng)]);

    if (latlngs.length >= 2) {
      L.polyline(latlngs, { color: '#be185d', weight: 4, opacity: 0.85 }).addTo(layer);
    }

    puntos.forEach((p, i) => {
      const isFirst = i === 0;
      const isLast = i === puntos.length - 1;
      const isSelected = i === selectedIndex;
      const color = isFirst ? '#059669' : isLast ? '#be185d' : '#6366f1';
      const radius = isSelected ? 9 : isFirst || isLast ? 7 : 5;
      const marker = L.circleMarker(latlngs[i], {
        radius,
        color: '#fff',
        weight: 2,
        fillColor: color,
        fillOpacity: 0.95,
      }).addTo(layer);

      marker.bindTooltip(
        `${isFirst ? 'Inicio · ' : isLast ? 'Último · ' : `#${i + 1} · `}${fmtHora(p.t)}`,
        { direction: 'top' }
      );
      marker.on('click', () => onSelect?.(i));
    });

    return undefined;
  }, [ready, puntos, selectedIndex, onSelect]);

  useEffect(() => {
    if (!ready) return;
    const L = window.L;
    const map = mapInstance.current;
    if (!L || !map || !puntos.length) return;
    const latlngs = puntos.map((p) => [Number(p.lat), Number(p.lng)]);
    if (latlngs.length === 1) {
      map.setView(latlngs[0], 16);
    } else {
      map.fitBounds(L.latLngBounds(latlngs), { padding: [36, 36], maxZoom: 17 });
    }
    setTimeout(() => map.invalidateSize(), 50);
  }, [ready, puntos]);

  if (error) return <p className="error banner">{error}</p>;
  return <div ref={mapRef} className="visita-map-canvas" role="img" aria-label="Mapa del historial GPS" />;
}

export default function VisitaMapModal({ visita, onClose }) {
  const puntos = useMemo(() => collectPuntos(visita), [visita]);
  const [selected, setSelected] = useState(Math.max(0, puntos.length - 1));
  const googleRuta = useMemo(() => routeUrl(puntos), [puntos]);
  const googleMapa = puntos.length
    ? mapsUrl(puntos[puntos.length - 1].lat, puntos[puntos.length - 1].lng)
    : null;

  useEffect(() => {
    setSelected(Math.max(0, puntos.length - 1));
  }, [visita?.id, puntos.length]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!visita) return null;

  return (
    <div
      className="pedido-detail-backdrop visita-map-modal-backdrop"
      onClick={(e) => {
        e.stopPropagation();
        onClose?.();
      }}
      role="presentation"
    >
      <div
        className="pedido-detail-modal visita-map-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Historial de ubicación"
      >
        <header className="pedido-detail-head">
          <div>
            <h3>Historial de ubicación</h3>
            <span className="visita-map-subtitle">
              {visita.vendedor_nombre || 'Vendedor'}
              {visita.sucursal_nombre || visita.sucursal_codigo
                ? ` · ${visita.sucursal_nombre || visita.sucursal_codigo}`
                : visita.live_only
                  ? ' · En app'
                  : ''}
            </span>
          </div>
          <button type="button" className="pedido-detail-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        {!puntos.length ? (
          <p className="muted">Esta visita aún no tiene puntos GPS.</p>
        ) : (
          <>
            <div className="visita-map-meta">
              <span>
                <strong>{puntos.length}</strong> punto{puntos.length !== 1 ? 's' : ''}
              </span>
              <span>
                {fmtHora(puntos[0].t)} → {fmtHora(puntos[puntos.length - 1].t)}
              </span>
              <span className="visita-map-external">
                {googleMapa ? (
                  <a href={googleMapa} target="_blank" rel="noreferrer">
                    Abrir en Google
                  </a>
                ) : null}
                {googleRuta && puntos.length >= 2 ? (
                  <>
                    {' · '}
                    <a href={googleRuta} target="_blank" rel="noreferrer">
                      Ruta Google
                    </a>
                  </>
                ) : null}
              </span>
            </div>

            <VisitaMapCanvas puntos={puntos} selectedIndex={selected} onSelect={setSelected} />

            <div className="visita-map-legend">
              <span>
                <i className="dot start" /> Inicio
              </span>
              <span>
                <i className="dot mid" /> Recorrido
              </span>
              <span>
                <i className="dot end" /> Último
              </span>
            </div>

            <div className="visita-map-timeline">
              <h4>Línea de tiempo</h4>
              <ol>
                {puntos.map((p, i) => {
                  const isFirst = i === 0;
                  const isLast = i === puntos.length - 1;
                  return (
                    <li key={`${p.t || i}-${i}`}>
                      <button
                        type="button"
                        className={`visita-map-point${selected === i ? ' active' : ''}`}
                        onClick={() => setSelected(i)}
                      >
                        <span className="visita-map-point-idx">
                          {isFirst ? 'Inicio' : isLast ? 'Último' : `#${i + 1}`}
                        </span>
                        <span className="visita-map-point-time">{fmtHora(p.t)}</span>
                        <span className="visita-map-point-coords">
                          {Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { collectPuntos, parsePuntos };
